import { create } from 'zustand'
import type { EstadoDaLigacao } from './clashPartilha'
import type { Presente } from './clashPartilhaCanal'

// ---------------------------------------------------------------------------
// O estado da triagem partilhada que a interface mostra. Store a parte
// (como o mobileUiStore): nao e trabalho, nao entra na recuperacao nem na
// sessao, e o motor escreve aqui o que a barra precisa de dizer.
//
// A triagem activa fica lembrada por workspace (localStorage), para um
// reload voltar a entrar nela sem perguntar.
// ---------------------------------------------------------------------------

export interface TriagemActiva {
  projetoId: string
  codigo: string
  nome: string | null
}

/** O que oferecer depois de abrir uma sessao: partilhar a triagem que
 *  ela trouxe, ou entrar na partilhada a que ela pertence. */
export type OfertaDePartilha =
  | { tipo: 'partilhar'; pareceres: number }
  | { tipo: 'entrar'; projetoId: string; codigo: string }

export interface EstadoDaPartilha {
  activa: TriagemActiva | null
  estado: EstadoDaLigacao
  /** Itens por enviar. */
  pendentes: number
  presentes: Presente[]
  /** O meu id no canal. */
  eu: number | null
  /** Pareceres partilhados de conflitos que nao estao na corrida deste
   *  browser (ou que ainda nao correu). */
  semCorrespondencia: number
  /** Pareceres locais sem GlobalId conhecido (so partilham depois de a
   *  deteccao voltar a correr). */
  semIdentidade: number
  erro: string | null
  oferta: OfertaDePartilha | null
  definir: (p: Partial<Omit<EstadoDaPartilha, 'definir'>>) => void
}

export const useClashPartilha = create<EstadoDaPartilha>((set) => ({
  activa: null,
  estado: 'parada',
  pendentes: 0,
  presentes: [],
  eu: null,
  semCorrespondencia: 0,
  semIdentidade: 0,
  erro: null,
  oferta: null,
  definir: (p) => set(p),
}))

const CHAVE_ACTIVA = 'vn2r.clash.partilha.activa'

function lerTodas(): Record<string, TriagemActiva> {
  try {
    const raw = localStorage.getItem(CHAVE_ACTIVA)
    const o: unknown = raw ? JSON.parse(raw) : {}
    return o && typeof o === 'object' ? (o as Record<string, TriagemActiva>) : {}
  } catch {
    return {}
  }
}

/** A triagem partilhada em que este browser estava, neste workspace. */
export function lerActiva(workspaceId: string): TriagemActiva | null {
  const t = lerTodas()[workspaceId]
  if (!t || typeof t.projetoId !== 'string' || typeof t.codigo !== 'string') return null
  return { projetoId: t.projetoId, codigo: t.codigo, nome: typeof t.nome === 'string' ? t.nome : null }
}

export function gravarActiva(workspaceId: string, t: TriagemActiva | null): void {
  try {
    const todas = lerTodas()
    if (t) todas[workspaceId] = t
    else delete todas[workspaceId]
    localStorage.setItem(CHAVE_ACTIVA, JSON.stringify(todas))
  } catch {
    /* sem armazenamento: fica so nesta pagina */
  }
}

/** Pessoas distintas na triagem (varios separadores da mesma conta
 *  contam uma vez). */
export function pessoasDistintas(presentes: readonly Presente[]): { nome: string; utilizador: string }[] {
  const vistos = new Map<string, { nome: string; utilizador: string }>()
  for (const p of presentes) {
    const k = p.utilizador || `#${p.id}`
    if (!vistos.has(k)) vistos.set(k, { nome: p.nome, utilizador: p.utilizador })
  }
  return [...vistos.values()]
}

/** Quem (alem de mim) esta a editar esta identidade agora. */
export function quemEdita(
  presentes: readonly Presente[],
  identidade: string | null,
  euUtilizador: string | null,
  agoraMs: number = Date.now(),
): string[] {
  if (!identidade) return []
  return [
    ...new Set(
      presentes
        .filter(
          (p) =>
            p.editar === identidade &&
            p.utilizador !== euUtilizador &&
            (p.editarAte === null || p.editarAte > agoraMs),
        )
        .map((p) => p.nome),
    ),
  ]
}
